import { Suspense, useEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { compose } from 'redux';
import Loader from '@karma/components/Loader';
import Alooma from 'services/alooma';
import * as ALOOMA from 'consts/alooma';
import useWisePops from 'services/useWisePops';
import useExtensionDetection from 'hooks/useExtensionDetection';
import useIOSExtensionDetection from 'hooks/useIOSExtensionDetection';
import useSetCjeInCookies from 'hooks/useSetCjeInCookies';
import useInitHotJar from 'services/useInitHotJar';
import { RetailersDBContextProvider } from 'contexts/RetailersDBContext';
import { ResolutionTypeContextProvider } from 'contexts/ResolutionTypeContext';
import AuthorizedRoute from 'components/organisms/AuthorizedRoute/AuthorizedRoute';
import UnauthorizedRoute from 'components/organisms/UnauthorizedRoute/UnauthorizedRoute';
import ServerRoutes from 'components/organisms/ServerRoutes/ServerRoutes';
import AppLayout from 'components/templates/AppLayout/AppLayout';
import { PATHS } from 'consts/paths';
import useComponentWillMount from 'hooks/useComponentWillMount';
import useSetLanguageFromQueryParam from 'hooks/useSetLanguageFromQueryParam';
import useInitIntercom from 'hooks/useInitIntercom';
import Bonuses from 'services/Bonuses';
import { CSR_ROUTES, SSR_ROUTES } from 'routes';
import useHeaderBannersPorter from 'hooks/useHeaderBannersPorter';
import useGetUserData from 'hooks/useGetUserData';
import useUpdateRetailersDB from 'hooks/useUpdateRetailersDB';
import configureApiRequests from 'functions/configureApiRequests';
import useGoogleTagManager from 'hooks/useGoogleTagManager';
import useAuthorizationListener from 'hooks/useAuthorizationListener';
import useSmartLook from 'hooks/useSmartLook';

const App = () => {
  const history = useHistory();

  useComponentWillMount(configureApiRequests);
  useExtensionDetection();
  useIOSExtensionDetection();
  useInitHotJar();
  useWisePops();
  useSmartLook();
  useSetLanguageFromQueryParam();
  useSetCjeInCookies();
  useGetUserData();
  useUpdateRetailersDB();
  useInitIntercom();
  useHeaderBannersPorter();
  useGoogleTagManager();
  useAuthorizationListener();

  Alooma.useClicksTracker();
  Bonuses.useInspector();

  useEffect(() => {
    const removeListener = history.listen(() => {
      window.scrollTo(0, 0);
    });

    return () => {
      removeListener();
    };
  }, [history]);

  return (
    <AppLayout>
      <Suspense fallback={<Loader absolute />}>
        <Switch>
          {ServerRoutes}

          {/* DASHBOARD PAGES */}
          <AuthorizedRoute
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.HOME}>
                <CSR_ROUTES.HomePage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            path={[PATHS.DASHBOARD, PATHS.HOME]}
          />

          <AuthorizedRoute
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.NEW_SALES}>
                <CSR_ROUTES.NewSalesPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            path={PATHS.NEW_SALES}
          />

          <AuthorizedRoute
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.TRAVEL}>
                <CSR_ROUTES.TravelPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            path={PATHS.TRAVEL}
          />

          <AuthorizedRoute
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.HOW_IT_WORKS}>
                <CSR_ROUTES.HowItWorksPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            path={PATHS.HOW_IT_WORKS}
          />

          <AuthorizedRoute
            render={routeProps => <CSR_ROUTES.ProfileEntryPage {...routeProps} />}
            path={PATHS.PROFILE}
          />

          {/* COUPONS */}
          <AuthorizedRoute
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.COUPONS}>
                <CSR_ROUTES.CouponsPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            exact
            path={PATHS.COUPONS}
          />

          <AuthorizedRoute
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.COUPONS__NEWEST}>
                <CSR_ROUTES.CouponsSeeAll {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            path={PATHS.COUPONS__NEWEST}
          />

          <AuthorizedRoute
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.COUPONS__POPULAR}>
                <CSR_ROUTES.CouponsSeeAll {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            path={PATHS.COUPONS__POPULAR}
          />

          {/* ITEMS */}
          <AuthorizedRoute
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.ITEMS}>
                <CSR_ROUTES.MySavedItemsPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            exact
            path={PATHS.ITEMS}
          />

          {/* STORES */}
          <AuthorizedRoute
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.STORES}>
                <CSR_ROUTES.StoresPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            path={PATHS.STORES}
          />

          <AuthorizedRoute path={PATHS.STORES__FEATURED} component={CSR_ROUTES.RetailersSeeAllPage} />
          <AuthorizedRoute path={PATHS.STORES__TRENDING} component={CSR_ROUTES.RetailersSeeAllPage} />
          <AuthorizedRoute path={PATHS.STORES__WE_ARE_LOVING} component={CSR_ROUTES.RetailersSeeAllPage} />

          <AuthorizedRoute
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.CASHBACK_PICKS}>
                <CSR_ROUTES.DashboardRetailersPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            path={PATHS.RETAILERS}
            exact
            relatedUnauthorizedLink={`${PATHS.KARMA_CASH}/$search`}
          />

          <AuthorizedRoute
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.DESIGNATED_STORE}>
                <CSR_ROUTES.DashboardRetailerPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            path={PATHS.RETAILER}
            relatedUnauthorizedLink={`${PATHS.KARMA_CASH}/$name/$id`}
          />

          <AuthorizedRoute
            path={PATHS.GLOBAL_SEARCH}
            exact
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.GLOBAL_SEARCH_RESULTS}>
                <CSR_ROUTES.GlobalSearchResultsPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />

          <Route
            path={PATHS.AUTH}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.OAUTH}>
                <CSR_ROUTES.OAuthPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />

          {/* REDIRECTS */}
          <Redirect from={PATHS.CASHBACK_PICKS} to={PATHS.KARMA_CASH} />
          <Redirect from={PATHS.CASHBACK_PICK} to={PATHS.KARMA_CASH_SINGLE} />

          <Route
            path={[PATHS.REDIRECT_IMAGEYE, PATHS.REDIRECT_SIAT]}
            component={CSR_ROUTES.RedirectForExternalClientPage}
          />

          <Route path={[PATHS.REDIRECT, PATHS.REF_INDEX, PATHS.REF_CAPTURE]} component={CSR_ROUTES.RedirectPage} />

          <Route path={PATHS.LOGOUT_AND_LOGIN} component={CSR_ROUTES.LogoutAndLoginPage} />

          <Route
            path={PATHS.DEMO.SIGN_UP}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.ONBOARDING_EXTENSION_SIGNUP}>
                <CSR_ROUTES.OnboardingExtensionSignup {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />

          <Route
            path={PATHS.DEMO.QUESTIONS}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.ONBOARDING_QUESTIONS}>
                <CSR_ROUTES.OnboardingExtensionQuestions {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />

          <Route
            path={PATHS.DEMO.SAFARI_STEP_1}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.ONBOARDING_SAFARI_STEP_1}>
                <CSR_ROUTES.OnboardingSafariStep1Page {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />

          <Route
            path={PATHS.DEMO.SAFARI_STEP_2}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.ONBOARDING_SAFARI_STEP_2}>
                <CSR_ROUTES.OnboardingSafariStep2Page {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />

          <Route
            path={PATHS.DEMO.SAFARI_SIGN_UP}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.ONBOARDING_SAFARI_SIGN_UP}>
                <CSR_ROUTES.OnboardingSafariSignUpPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />

          <Route
            path={PATHS.DEMO.CARD_COMPARISON}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.DEMO_CARD_COMPARISON}>
                <CSR_ROUTES.DemoCardComparisonPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />

          <Route
            path={PATHS.DESKTOP_SWE_LOGIN}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.DESKTOP_SWE_LOGIN}>
                <CSR_ROUTES.DesktopSweLogin {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <Route
            path={PATHS.CONFIRM_WITHDRAWAL}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.CONFIRM_WITHDRAWAL}>
                <CSR_ROUTES.ConfirmWithdrawalPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <Route
            path={PATHS.ONBOARDING.IOS_EXTENSION}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.SWE_ONBOARDING}>
                <CSR_ROUTES.OnboardingIOSExt {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />

          {/* to keep backward compatibility for old mobile devices. do not change to <Redirect /> to not lose query params */}
          <Route
            path={PATHS.ONBOARDING.IOS_EXTENSION_V2}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.SWE_ONBOARDING}>
                <CSR_ROUTES.OnboardingIOSExt {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />

          <Route
            path={PATHS.UNSUBSCRIBE}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.UNSUBSCRIBE}>
                <CSR_ROUTES.UnsubscribePage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <Route
            path={PATHS.UNSUBSCRIBED}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.UNSUBSCRIBED}>
                <CSR_ROUTES.UnsubscribeResult {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <Route
            path={PATHS.DISABLING_TAGS}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.UNSUBSCRIBE_TAG}>
                <CSR_ROUTES.UnsubscribeTagPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <Route
            path={PATHS.MUTE_TAG}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.MUTE_TAG}>
                <CSR_ROUTES.MuteTagPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <Route
            path={PATHS.DATA_DELETED}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.DATA_DELETED}>
                <CSR_ROUTES.UsedDataDeletedPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <UnauthorizedRoute
            exact
            path={PATHS.WEBSITE_LANDING_BRB}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.LANDING_MAIN_OLD}>
                <SSR_ROUTES.WebsiteLandingKarmaOld {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            relatedAuthorizedLink={PATHS.DASHBOARD}
          />
          <UnauthorizedRoute
            exact
            path={PATHS.WEBSITE_LANDING_JOIN}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.LANDING_MAIN_JOIN}>
                <SSR_ROUTES.WebsiteLandingKarmaOld {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
            relatedAuthorizedLink={PATHS.DASHBOARD}
          />
          <UnauthorizedRoute
            exact
            path={PATHS.WEBSITE_LANDING_MEN}
            render={routeProps => <CSR_ROUTES.WebsiteLandingKarmaForMen {...routeProps} />}
            relatedAuthorizedLink={PATHS.DASHBOARD}
          />
          <UnauthorizedRoute
            exact
            path={PATHS.WEBSITE_LANDING_MARKETING_5}
            render={routeProps => (
              <CSR_ROUTES.WebsiteLandingKarma {...routeProps} redirectPath={PATHS.WEBSITE_LANDING_MARKETING_5} />
            )}
            relatedAuthorizedLink={PATHS.DASHBOARD}
          />
          <Route
            path={PATHS.PARTNERSHIPS_V1}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.PARTNERSHIPS}>
                <CSR_ROUTES.PartnershipsOldPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <Route
            path={PATHS.EMAIL_NEGATIVE_FEEDBACK}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.EMAIL_NEGATIVE_FEEDBACK}>
                <CSR_ROUTES.EmailNegativeFeedback {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <Route
            path={PATHS.EMAIL_NEGATIVE_FEEDBACK_SUCCESS}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.EMAIL_NEGATIVE_FEEDBACK_SUCCESS}>
                <CSR_ROUTES.EmailNegativeFeedbackSuccess {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <Route
            path={PATHS.EMAIL_POSITIVE_FEEDBACK}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.EMAIL_POSITIVE_FEEDBACK}>
                <CSR_ROUTES.EmailPositiveFeedback {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <Route
            path={PATHS.EXTENSION_DELETED}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.EXTENSION_DELETED}>
                <CSR_ROUTES.ExtensionDeletedPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <Route
            path={PATHS.EXTENSION_DELETED_SUCCESS}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.EXTENSION_DELETED_SUCCESS}>
                <CSR_ROUTES.ExtensionDeletedPageSuccess {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <Route
            path={PATHS.MOBILE_LOGIN}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.MOBILE_LOGIN}>
                <CSR_ROUTES.MobileLoginRedirectPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <UnauthorizedRoute
            path={PATHS.MOBILE_SIGNUP}
            relatedAuthorizedLink={PATHS.KARMA_EARN_LIST}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.MOBILE_SIGNUP}>
                <CSR_ROUTES.MobileSignupPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <AuthorizedRoute
            exact
            path={PATHS.KARMA_EARN_LIST}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.KARMA_EARN_LIST}>
                <CSR_ROUTES.KarmaEarnListPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <AuthorizedRoute
            path={PATHS.KARMA_EARN_GAME}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.KARMA_EARN_GAME}>
                <CSR_ROUTES.KarmaEarnGamePage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <AuthorizedRoute
            path={PATHS.KARMA_EARN_OFFER}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.KARMA_EARN_OFFER}>
                <CSR_ROUTES.KarmaEarnOfferPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <AuthorizedRoute
            path={PATHS.KARMA_EARN_ITEM_NOT_FOUND}
            relatedUnauthorizedLink={PATHS.KARMA_EARN_ITEM_NOT_FOUND_UNAUTHORIZED}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.KARMA_EARN_ITEM_NOT_FOUND}>
                <CSR_ROUTES.KarmaEarnItemNotFoundPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
          <UnauthorizedRoute
            path={PATHS.KARMA_EARN_ITEM_NOT_FOUND_UNAUTHORIZED}
            relatedAuthorizedLink={PATHS.KARMA_EARN_ITEM_NOT_FOUND}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.KARMA_EARN_ITEM_NOT_FOUND}>
                <CSR_ROUTES.KarmaEarnItemNotFoundUnauthorizedPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />

          {/* NOT FOUND */}
          <Route
            path={[PATHS.NGINX_NOT_FOUND, PATHS.NOT_FOUND, '*']}
            render={routeProps => (
              <Alooma.SourceContext.Provider value={ALOOMA.SOURCE_PAGES.NOT_FOUND}>
                <CSR_ROUTES.ErrorPage {...routeProps} />
              </Alooma.SourceContext.Provider>
            )}
          />
        </Switch>
      </Suspense>
    </AppLayout>
  );
};

export default compose(RetailersDBContextProvider(), ResolutionTypeContextProvider())(App);
